import { z } from 'zod';
import type { MatrixType, RiskMatrixConfiguration } from '../types/business/risk';

/**
 * Risk matrix configuration — the runtime contract for the `RISK_MATRIX`
 * organisation configuration item, validated at the tRPC boundary
 * (`risk.upsertRiskMatrixConfiguration`).
 *
 * Design notes:
 * - **Arrays are bounded by slots, not by the active matrix dimension.** When a
 *   matrix is downsized (5x5 → 3x3) the client deliberately retains the hidden
 *   entries so upsizing restores them (`RiskMatrixContext.handleMatrixTypeChange`
 *   only ever grows arrays; rendering slices to the active dimension). Every
 *   dimension-bearing array therefore allows up to `MAX_RISK_MATRIX_SLOTS`
 *   entries regardless of `matrixType`, and must hold at least the active
 *   dimension.
 * - **Unknown keys are stripped, not rejected.** The server persists the parsed
 *   result, so stray keys on legacy tenant rows are dropped from storage rather
 *   than failing a legitimate editor's save. Storage stays bounded either way.
 * - `riskAppetiteMatrixData` cells hold `RiskAppetite` row ids (positive
 *   integers) or `null`; `riskImpactDescriptionsMatrixData` is keyed by numeric
 *   `RiskCategory` ids.
 */
export const MAX_RISK_MATRIX_SLOTS = 5;
export const MAX_RISK_MATRIX_LABEL_LENGTH = 200;
export const MAX_RISK_MATRIX_DESCRIPTION_LENGTH = 5_000;
export const MAX_RISK_MATRIX_CATEGORY_KEYS = 500;
export const MAX_RISK_MATRIX_ENCODED_BYTES = 1_048_576; // 1 MiB (UTF-8)

export const RISK_MATRIX_TYPES = ['3x3', '4x4', '5x5'] as const satisfies readonly MatrixType[];

const matrixLabelSchema = z.string().max(MAX_RISK_MATRIX_LABEL_LENGTH);
const matrixDescriptionSchema = z.string().max(MAX_RISK_MATRIX_DESCRIPTION_LENGTH);
const isoTimestampSchema = z.string().datetime({ offset: true });

const riskLikelihoodSchema = z.object({
    id: matrixLabelSchema,
    name: matrixLabelSchema,
    order: z.number().int(),
    description: matrixDescriptionSchema,
    probability: matrixLabelSchema,
    time: matrixLabelSchema,
});

export const riskMatrixConfigurationSchema = z
    .object({
        matrixType: z.enum(RISK_MATRIX_TYPES),
        impacts: z.array(matrixLabelSchema).max(MAX_RISK_MATRIX_SLOTS),
        likelihoods: z.array(riskLikelihoodSchema).max(MAX_RISK_MATRIX_SLOTS),
        riskImpactDescriptionsMatrixData: z
            // Keys are numeric RiskCategory ids — a bigint is at most 20 digits.
            .record(z.string().max(20).regex(/^\d+$/), z.array(matrixDescriptionSchema).max(MAX_RISK_MATRIX_SLOTS))
            .refine((data) => Object.keys(data).length <= MAX_RISK_MATRIX_CATEGORY_KEYS, {
                message: `Too many category description entries (max ${MAX_RISK_MATRIX_CATEGORY_KEYS}).`,
            }),
        riskAppetiteMatrixData: z
            .array(z.array(z.number().int().positive().nullable()).max(MAX_RISK_MATRIX_SLOTS))
            .max(MAX_RISK_MATRIX_SLOTS),
        // Server-managed stamps; legacy configs may omit them entirely.
        lastUpdated: isoTimestampSchema.nullish().transform((value) => value ?? null),
        lastSynced: isoTimestampSchema.nullish(),
    })
    .superRefine((config, ctx) => {
        const dimension = Number(config.matrixType[0]);

        if (config.impacts.length < dimension) {
            ctx.addIssue({
                code: 'custom',
                path: ['impacts'],
                message: `A ${config.matrixType} matrix requires at least ${dimension} impacts.`,
            });
        }

        if (config.likelihoods.length < dimension) {
            ctx.addIssue({
                code: 'custom',
                path: ['likelihoods'],
                message: `A ${config.matrixType} matrix requires at least ${dimension} likelihoods.`,
            });
        }

        if (
            config.riskAppetiteMatrixData.length < dimension ||
            config.riskAppetiteMatrixData.some((row) => row.length < dimension)
        ) {
            ctx.addIssue({
                code: 'custom',
                path: ['riskAppetiteMatrixData'],
                message: `A ${config.matrixType} matrix requires at least ${dimension} appetite rows of ${dimension} cells.`,
            });
        }

        for (const [categoryId, descriptions] of Object.entries(config.riskImpactDescriptionsMatrixData)) {
            if (descriptions.length < dimension) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['riskImpactDescriptionsMatrixData', categoryId],
                    message: `A ${config.matrixType} matrix requires at least ${dimension} impact descriptions per category.`,
                });
            }
        }

        // Measure UTF-8 bytes, not UTF-16 code units — multibyte text is up to
        // 3× longer encoded than `String.length` reports.
        if (new TextEncoder().encode(JSON.stringify(config)).byteLength > MAX_RISK_MATRIX_ENCODED_BYTES) {
            ctx.addIssue({
                code: 'custom',
                message: 'Risk matrix configuration exceeds the maximum allowed size.',
            });
        }
    });

// Pin the schema output to the shared compile-time type so the two cannot drift.
export type RiskMatrixConfigurationInput = z.input<typeof riskMatrixConfigurationSchema>;
type SchemaOutput = z.output<typeof riskMatrixConfigurationSchema>;
type _SchemaMatchesType = SchemaOutput extends RiskMatrixConfiguration ? true : never;
const _schemaMatchesType: _SchemaMatchesType = true;
void _schemaMatchesType;
